import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, apiRequest, type RequestOptions } from './api.js';

/**
 * Data-fetching primitives.
 *
 * Deliberately hand-rolled rather than pulled from a library. The application
 * needs four things — a loading state, an error state, a manual refetch, and
 * cancellation on unmount — and those are twenty lines. A query library would
 * add a cache to reason about, a provider to wrap, and a set of behaviours that
 * have to be learned before the code can be read.
 *
 * ## Aborting on unmount is not optional
 *
 * Without the abort, navigating away from a slow request leaves a promise that
 * resolves into a component that no longer exists. React 19 no longer warns
 * about the state update, so the bug becomes silent: a stale response can
 * overwrite fresher data after a fast back-and-forward navigation. Passing the
 * signal through to `fetch` fixes it at the source.
 */

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  refetch: () => void;
}

export function useApi<T>(path: string | null, options: RequestOptions = {}): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  // The options object is a new reference on every render; keeping the latest
  // in a ref means the effect can depend on the serialised path only, instead of
  // re-fetching on every parent render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    apiRequest<T>(path, { ...optionsRef.current, signal: controller.signal })
      .then((result) => {
        if (!active) return;
        setData(result);
      })
      .catch((caught: unknown) => {
        if (!active) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError(0, {
                type: 'https://harbor.dev/errors/unknown',
                title: 'Unexpected Error',
                status: 0,
                detail: caught instanceof Error ? caught.message : 'Something went wrong',
              }, null),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [path, nonce]);

  const refetch = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, refetch };
}

/**
 * Debounce a rapidly changing value.
 *
 * Used by the catalogue search box. Typing "desk lamp" fires nine keystrokes;
 * without this, that is nine requests and nine renders, and the responses can
 * land out of order.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Persisted theme, with the system preference as the default. */
export type Theme = 'light' | 'dark';

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.style.colorScheme = next;
      try {
        localStorage.setItem('harbor-theme', next);
      } catch {
        // Storage can be unavailable in private mode. The theme still applies
        // for this session; only the preference is lost.
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}

/**
 * Locks body scroll while a modal or drawer is open.
 *
 * Restores the previous value rather than clearing the style outright, so nested
 * overlays do not leave the page permanently unscrollable — a bug that is very
 * easy to introduce and very annoying to hit.
 */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [locked]);
}

/** Calls `handler` on Escape, for dialogs and drawers. */
export function useEscape(handler: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handler();
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handler, enabled]);
}
